import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { type EvidenceOut, type FieldResultOut } from "@platform/api/golden";
import {
  Alert,
  Button,
  MenuItem,
  Select,
  TextField,
} from "@platform/design-system";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  CORRECT_VALUE_REQUIRED,
  isEmptyValue,
  parseTypedValue,
} from "../shared/values";

import {
  answersAnAsk,
  type DiscrepancyVerdict,
  reasonPrompt,
  REASON_REQUIRED,
  VERDICT_LABELS,
  verdictsFor,
} from "./verdicts";

import type { ReactNode } from "react";

export const VERDICT_FORM_TEST_ID = "golden-verdict-form";

/** What a reviewer decided about one discrepancy. */
export interface Verdict {
  readonly verdict: DiscrepancyVerdict;
  /** Absent when the reviewer gave none. */
  readonly reason: string | undefined;
  /** The value both should have had; only a both_wrong verdict carries one. */
  readonly correctedValue: unknown;
}

const verdictSchema = z
  .object({
    verdict: z.custom<DiscrepancyVerdict>((value) => typeof value === "string"),
    reason: z.string().trim(),
    correctValue: z.string(),
  })
  .superRefine(({ verdict, reason, correctValue }, context) => {
    if (answersAnAsk(verdict) && reason === "") {
      context.addIssue({
        code: "custom",
        path: ["reason"],
        message: REASON_REQUIRED,
      });
    }
    // The service stores no correction to an empty value, so an untouched field, or
    // one holding null, "", [] or {}, is refused here rather than by the API.
    if (
      verdict === "both_wrong" &&
      isEmptyValue(parseTypedValue(correctValue))
    ) {
      context.addIssue({
        code: "custom",
        path: ["correctValue"],
        message: CORRECT_VALUE_REQUIRED,
      });
    }
  });

type VerdictValues = z.infer<typeof verdictSchema>;

/**
 * Records a verdict on a discrepancy, or changes one, offering only the verdicts the
 * API accepts for it. A verdict on RUMI's ask needs its answer as the reason.
 */
export const VerdictForm = ({
  result,
  evidence,
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  readonly result: FieldResultOut;
  readonly evidence: readonly EvidenceOut[];
  readonly initial: {
    readonly verdict: DiscrepancyVerdict;
    readonly reason: string;
    /** The stored correction, as typed text, when changing a both_wrong verdict. */
    readonly correctValue?: string;
  };
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly error: string | undefined;
  readonly onSubmit: (verdict: Verdict) => void;
  readonly onCancel?: () => void;
}): ReactNode => {
  const { control, handleSubmit } = useForm<VerdictValues>({
    resolver: zodResolver(verdictSchema),
    defaultValues: {
      verdict: initial.verdict,
      reason: initial.reason,
      correctValue: initial.correctValue ?? "",
    },
  });
  const verdict = useWatch({ control, name: "verdict" });

  const submit = handleSubmit((values) =>
    onSubmit({
      verdict: values.verdict,
      reason: values.reason === "" ? undefined : values.reason,
      correctedValue:
        values.verdict === "both_wrong"
          ? parseTypedValue(values.correctValue)
          : undefined,
    }),
  );

  return (
    <Box
      component="form"
      noValidate
      data-testid={VERDICT_FORM_TEST_ID}
      onSubmit={(event) => void submit(event)}
      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* The design-system fields forward their ref to the wrapper, not the input,
            so they are controlled rather than registered. */}
        <Controller
          control={control}
          name="verdict"
          render={({ field: { ref: _ref, ...field } }) => (
            <Select
              data-testid={`${VERDICT_FORM_TEST_ID}-verdict`}
              label="Verdict"
              sx={{ minWidth: 340 }}
              {...field}
            >
              {verdictsFor(result, evidence).map((option) => (
                <MenuItem key={option} value={option}>
                  {VERDICT_LABELS[option]}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        {verdict === "both_wrong" && (
          <Controller
            control={control}
            name="correctValue"
            render={({ field: { ref: _ref, ...field }, fieldState }) => (
              <TextField
                data-testid={`${VERDICT_FORM_TEST_ID}-correct-value`}
                label="Correct value"
                error={fieldState.error !== undefined}
                helperText={
                  fieldState.error?.message ??
                  "Text, or JSON for a number, list, or object"
                }
                {...field}
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="reason"
          render={({ field: { ref: _ref, ...field }, fieldState }) => (
            <TextField
              data-testid={`${VERDICT_FORM_TEST_ID}-reason`}
              label={reasonPrompt(verdict)}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
              sx={{ flex: 1, minWidth: 260 }}
              {...field}
            />
          )}
        />
      </Box>
      {error && (
        <Alert data-testid={`${VERDICT_FORM_TEST_ID}-error`} severity="error">
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          type="submit"
          data-testid={`${VERDICT_FORM_TEST_ID}-submit`}
          loading={pending}
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="secondary" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
};
